import { useEffect } from "react";
import { Helmet } from "react-helmet";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { useToast } from "barte-design-system";
import { usePostHog } from "@posthog/react";

import { HeaderPage } from "@shared/components";
import { TRACK_EVENTS } from "@payfac/constants";
import { useGetSellerRates, useUpdateSellerRates } from "@payfac/services";
import type { ISellerRates } from "@payfac/types";

import { RatesDetails } from "./components/rates-details";
import styles from "./rates.module.css";

export const Rates = () => {
    const { t } = useTranslation();
    const toast = useToast();
    const posthog = usePostHog();

    const [searchParams] = useSearchParams();
    const idSeller = searchParams.get("idSeller") ?? "";

    const { data: response, isLoading } = useGetSellerRates({ sellerId: idSeller });
    const { mutateAsync: updateRates, isPending } = useUpdateSellerRates();

    const data = response?.data;

    const onSubmitData = async (
        sellerId: string,
        payload: ISellerRates,
        onSuccess?: () => void,
    ) => {
        posthog.capture(TRACK_EVENTS.RATES.UPDATED_INTENT);

        const res = await updateRates({
            sellerId,
            payload: payload.currentSellerRatesConfig,
            receiptType: payload.receiptType,
        });

        if (res.success) {
            posthog.capture(TRACK_EVENTS.RATES.UPDATED_SUCCESS);

            toast.success({ title: res?.message });
            onSuccess?.();
        } else {
            posthog.capture(TRACK_EVENTS.RATES.UPDATED_ERROR, {
                error: res?.message
            });

            toast.error({
                title: t("payfac.pageRatesUpdateError"),
                description: res?.message,
            });
        }
    };

    const onImportData = async (rawPayload: Record<string, any>, onSuccess: () => void) => {
        posthog.capture(TRACK_EVENTS.RATES.IMPORTED_INTENT);

        const res = await updateRates({ sellerId: idSeller, rawPayload });

        if (res.success) {
            posthog.capture(TRACK_EVENTS.RATES.IMPORTED_SUCCESS);

            toast.success({ title: res?.message });
            onSuccess();
        } else {
            posthog.capture(TRACK_EVENTS.RATES.IMPORTED_ERROR, {
                error: res?.message
            });

            toast.error({
                title: t("payfac.pageRatesUpdateError"),
                description: res?.message,
            });
        }
    };

    useEffect(() => {
        if (idSeller) {
            posthog.capture(TRACK_EVENTS.RATES.VIEWED, { sellerId: idSeller });
        }
    }, [idSeller]);

    if (!data && !isLoading) {
        return <div>{t("payfac.pageRatesSellerNotFound")}</div>;
    }

    return (
        <div
            className={styles.container}
        >
            <Helmet>
                <title>
                    {idSeller
                        ? `${t("payfac.pageRatesTitle")} #${idSeller} - Barte`
                        : "Barte"}
                </title>
            </Helmet>

            <HeaderPage
                title={`${t("payfac.pageRatesTitle")} #${idSeller}`}
                isLoading={isLoading}
                withBackButton
            />

            <RatesDetails
                data={data}
                isLoading={isLoading}
                onSubmitData={onSubmitData}
                onImportData={onImportData}
                isPending={isPending}
                sellerId={idSeller}
            />
        </div>
    );
};
